import { Button, Checkbox, Theme, Grid } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import CheckMarkIcon from '@icons/CheckMarkIcon'
import clsx from 'clsx'
import { FormattedMessage, useIntl } from 'react-intl'
import messages from '@configurator-messages'
import React, { useState } from 'react'
import { genomicsRoute, loginRoute, useRouter } from '@src/routes'
import { ConfigContext } from '../../Genomics'
import { priceFormatter } from '@src/routes/Configurators/utils/PriceFormatter'
import {
  SpecificationMessages,
  GenomicsProperties,
  ErrorMessage,
  ActiveLinkType,
} from '../../enum'
import {
  useConfAddToMiniCartMutation,
  useCustomCrisprSaveToSessionMutation,
} from '@src/mutations/ConfiguratorTubesMutation.generated'
import AddToCartItemsMTO from '@src/routes/Configurators/components/AddToCart/AddToCartItemsMTO'
import {
  SnackbarType,
  useGlobalSnackbar,
} from '@src/components/GlobalSnackbar/globalSnackBarContext'
import CircleLoader from '@src/components/CircleLoader'
import {
  handleGAAddToCartForConfigurator,
  handleGAAddToCartErrorsForConfigurator,
} from '@utils/analytics/configuratorAnalytics'
import {
  ProductNames,
  messageRegexPattern,
  ConfiguratorValues,
} from '@src/routes/Configurators/utils/constants'
import { checkValidKey } from '@src/routes/Configurators/utils/util'
import { SpecificationsProps } from '@src/routes/Configurators/components/ConfiguratorTypes'
import SpecificationItem from './SpecificationItem'
import DownloadSummary from './DownloadSummary'
import ValidationErrors from '@src/routes/Configurators/validations/ValidationErrors'
import {
  CartRequestAction,
  ConfiguratorAddToMiniCartInput,
} from '@src/types/graphql-types'
import { QUICK_CART } from '@src/queries/QuickCartQuery'

const useStyles = makeStyles((theme: Theme) => {
  return {
    contentWrapper: {
      backgroundColor: theme.palette.common.white,
      marginTop: theme.spacing(8),
    },
    paddingBott: {
      paddingBottom: theme.spacing(4),
    },
    acknowledgeSpace: {
      fontSize: theme.typography.pxToRem(14),
      padding: theme.spacing(6, 0, 0, 0),
    },
    checkBoxAcknowledge: {
      padding: theme.spacing(2, 0),
    },
    shippingInfo: {
      fontSize: theme.typography.pxToRem(12),
    },
    sectionHeader: {
      fontSize: theme.typography.pxToRem(18),
      fontWeight: 900,
      marginBottom: theme.spacing(4),
    },
    subTotal: {
      fontSize: theme.typography.pxToRem(18),
      fontWeight: 900,
      borderBottom: `1px solid ${theme.palette.grey[500]}`,
      paddingBottom: theme.spacing(2),
      marginBottom: theme.spacing(2),
    },
    addToCart: {
      marginTop: theme.spacing(6),
      marginBottom: theme.spacing(4),
    },
    checkbox: {
      fontSize: theme.typography.pxToRem(10),
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      height: theme.typography.pxToRem(18),
      width: theme.typography.pxToRem(18),
      border: `solid 1px ${theme.palette.grey[500]}`,
      borderRadius: theme.typography.pxToRem(3),
      transition: 'all .3s',
    },
    errorBorder: {
      border: `solid 2px ${theme.palette.rich.red}`,
    },
  }
})
interface CrisprDetailsForGA {
  productDetails: any
  totalQuantity: number
  price: string
  productName: string
}

const Specifications: React.FC<SpecificationsProps> = ({
  productDetails,
  itemDetails,
  currencyFormat,
  updateError,
  thousandSeparator,
  thousandSpacing,
  prefixCurrency,
  decimalSeparator,
}) => {
  const classes = useStyles()
  const intl = useIntl()
  const router = useRouter()
  const { state, dispatch } = React.useContext(ConfigContext)
  const { sessionData, companionProducts } = state
  const [priceForGA, setPriceForGA] = useState<number>(1)
  const [subTotalPrice, setSubTotalPrice] = useState<string>('')
  const [totalPrice, setTotalPrice] = useState<string>('')
  const [dialogOpen, setDialogOpen] = React.useState<boolean>(false)
  const { setGlobalSnackbarState } = useGlobalSnackbar()
  const [totalItem, setTotalItem] = useState<number>(0)
  const [acknowledgement, setAcknowledgement] = React.useState<boolean>(false)
  const [agreeTermsError, setAgreeTermsError] = React.useState<boolean>(false)
  const [addToMiniCart, { data, loading }] = useConfAddToMiniCartMutation({})
  const [customCrisprSaveToSession] = useCustomCrisprSaveToSessionMutation()

  const configuratorAddToMiniCart: ConfiguratorAddToMiniCartInput = {
    productType: productDetails.productType,
    countryCode: productDetails.countryCode,
    participantId: productDetails.participantId,
    locale: productDetails.locale,
    sapNextEnabled: productDetails.sapNextEnabled,
    timeZone: productDetails.timeZone,
    store: productDetails.store,
  }

  const companionProductsData =
    companionProducts?.length && companionProducts[0] !== ''
      ? companionProducts
      : null
  let subTotal
  const initialSessionData = () => {
    return [
      {
        id: 1,
        isOverridden: false,
        productType: 'customcrispr',
        terms: false,
        listSubModels: [
          {
            name: '',
            geneId: '',
            geneSymbol: '',
            sequence: '',
            id: 1,
            isOverridden: false,
            baseCount: 0,
            isCurrentForm: true,
            cartId: '',
            itemId: '',
          },
        ],
        physicalMaterial: '',
        format: 'DRY',
        pageNo: 0,
        purification: '',
        structure: '',
        modification: '',
        scale: '',
        species: '',
        listVectors: [''],
        listAmount: [''],
        listLentiviralBackbones: [''],
        listViralTiters: [''],
        listVolumes: [''],
        listAliquotings: [''],
        listPurification: [''],
        listSpecies: [''],
        listScale: [''],
        listModification: [''],
        listStructure: [''],
        listPhysicalMaterials: [],
        price: '',
        currencySymbol: '',
        currency: '',
        modifiedSequence: '',
        modifiedTechnicalSequence: '',
      },
    ]
  }

  //configuring the Configurator product data for sending it to GA
  const itemName = ProductNames['customcrispr']
  const qty = sessionData[0].listSubModels.length - 1
  const avgPrice = (priceForGA / qty).toFixed(Number(currencyFormat))

  let gaPrice
  let gaFormattedPrice
  if (avgPrice) {
    try {
      gaPrice = avgPrice.slice(0, avgPrice.length - 3).replace(/[.,]/g, '')
      gaFormattedPrice =
        gaPrice + avgPrice.slice(avgPrice.length - 3, avgPrice.length)
    } catch {
      gaFormattedPrice = avgPrice
    }
  } else {
    gaFormattedPrice = avgPrice
  }
  const configProductDetails: CrisprDetailsForGA = {
    productDetails: itemDetails,
    totalQuantity: qty,
    price: `${gaFormattedPrice}`,
    productName: itemName,
  }
  const calculatePrice = () => {
    subTotal = 0
    let isPriceValid = false
    if (sessionData[0].listSubModels[0].price === GenomicsProperties.Quote) {
      setSubTotalPrice(intl.formatMessage(messages._PC_QUOTE_ITEM))
      setPriceForGA(1 * qty)
    } else {
      sessionData[0].listSubModels.forEach((item) => {
        if (sessionData[0].listSubModels[0].price) {
          if (!item.isCurrentForm) {
            subTotal += parseFloat(sessionData[0].listSubModels[0].price)
            isPriceValid = true
          }
        } else {
          setSubTotalPrice('--')
          isPriceValid = false
        }
      })
      if (isPriceValid) {
        const formattedPrice = priceFormatter(
          subTotal,
          currencyFormat,
          thousandSeparator,
          thousandSpacing,
          decimalSeparator,
          prefixCurrency,
          sessionData[0].currencySymbol
        )
        const finalPrice = formattedPrice
        setSubTotalPrice(finalPrice)
        setPriceForGA(subTotal)
      }
      // For GA if price is not there
      else {
        setPriceForGA(1 * qty)
      }
    }
  }
  const saveSessionDataToSession = async (obj) => {
    await customCrisprSaveToSession({
      variables: { input: obj, productInput: productDetails },
    })
  }
  const handleAddToCart = async () => {
    await addToMiniCart({
      variables: { input: configuratorAddToMiniCart },
      fetchPolicy: 'no-cache',
      refetchQueries: [
        {
          query: QUICK_CART,
          variables: { id: CartRequestAction.Mini },
        },
      ],
    })
  }
  const addToCart = () => {
    if (
      state.sessionData[0].physicalMaterial === SpecificationMessages.SRNA &&
      state.sessionData[0].structure === SpecificationMessages.CRRNA
    ) {
      if (acknowledgement) {
        handleAddToCart()
      } else {
        setAgreeTermsError(true)
      }
    } else {
      handleAddToCart()
    }
  }
  const clearSessionData = () => {
    saveSessionDataToSession([])
    dispatch({
      type: 'UPDATE_CURRENT_INDEX',
      data: 0,
    })
    dispatch({
      type: 'UPDATE_LIST_SUB_MODEL',
      data: [],
    })
    dispatch({
      type: 'UPDATE_INPUT',
      data: initialSessionData(),
    })
    dispatch({
      type: 'SET_PAGE_ERROR',
      data: '',
    })
    dispatch({
      type: 'UPDATE_ERRORS',
      data: [],
    })
    dispatch({
      type: 'SET_STEP_NO',
      data: 0,
    })
  }
  const sequencePath = `${genomicsRoute.index()}?product=${
    productDetails.productType
  }&activeLink=${ActiveLinkType.SequenceEntry}`
  const [isUpdateCart, setIsUpdateCart] = useState<boolean>(false)

  React.useEffect(() => {
    if (data?.confAddToMiniCart?.result !== GenomicsProperties.Success) {
      calculatePrice()
    }
    setAcknowledgement(sessionData[0].terms)
    const cartItems = sessionData.filter((item) => {
      return item?.cartId?.length && item?.itemId?.length
    })
    cartItems.length ? setIsUpdateCart(true) : setIsUpdateCart(false)
    // TODO: need a test before we refactor this
  }, [sessionData])
  React.useEffect(() => {
    if (data) {
      const errorMessage = ConfiguratorValues.AddToCartFail
      if (data?.confAddToMiniCart?.serverError) {
        //GAtracking event on button click of addToCart
        updateError(ConfiguratorValues.AddToCartFail)
        handleGAAddToCartErrorsForConfigurator(
          configProductDetails,
          errorMessage
        )
      } else {
        if (data?.confAddToMiniCart?.result === GenomicsProperties.Success) {
          //GAtracking event on button click of addToCart
          handleGAAddToCartForConfigurator(configProductDetails)
          setTotalItem(sessionData[0].listSubModels.length - 1)
          setTotalPrice(subTotalPrice)
          setSubTotalPrice('--')
          clearSessionData()
          setDialogOpen(true)
        } else if (
          data?.confAddToMiniCart?.result === ErrorMessage.SessionDataEmpty
        ) {
          clearSessionData()
          router.push(sequencePath)
        } else if (
          data?.confAddToMiniCart?.result === ErrorMessage.AccessTokenExpire
        ) {
          router.push(loginRoute.index())
          setGlobalSnackbarState({
            open: true,
            message: (
              <FormattedMessage
                {...messages.USER_NOT_AUTHORIZED_OR_TOKEN_EXPIRED}
              />
            ),
            variant: SnackbarType.Error,
          })
        } else {
          updateError(ConfiguratorValues.AddToCartFail)
          handleGAAddToCartErrorsForConfigurator(
            configProductDetails,
            errorMessage
          )
        }
      }
    }
    // TODO: need a test before we refactor these dependencies
  }, [data])

  const handleAcknowledgement = (value) => {
    setAcknowledgement(value)
    setAgreeTermsError(false)
    sessionData.forEach((crispr) => {
      crispr.terms = value
    })
    dispatch({
      type: 'UPDATE_INPUT',
      data: [...state.sessionData],
    })
    saveSessionDataToSession(sessionData)
  }
  const handleDialogClose = () => {
    setDialogOpen(false)
    router.push(sequencePath)
  }

  return (
    <>
      <div className={classes.contentWrapper}>
        <Grid container spacing={2}>
          <Grid item sm={7}>
            <Grid item sm={12} className={classes.sectionHeader}>
              <FormattedMessage {...messages.CONF_SPECIFICATIONS} />
            </Grid>
            {state.sessionData[0].physicalMaterial ===
            SpecificationMessages.DNA ? (
              <>
                <SpecificationItem
                  label={intl.formatMessage(
                    messages.CONF_SPECIFICATION_PHYSICAL_MATERIAL
                  )}
                  value={intl.formatMessage(
                    messages.PC_SPEC_PHYSICAL_MATERIAL_DNA
                  )}
                />
                <SpecificationItem
                  label={intl.formatMessage(
                    messages.CONF_DNA_SPECIFICATION_crispgrna
                  )}
                  value={
                    sessionData[0].vector
                      ? intl.formatMessage(
                          checkValidKey(
                            messages[
                              `PC_SPEC_VECTOR_${sessionData[0].vector.replace(
                                messageRegexPattern,
                                '_'
                              )}`
                            ],
                            `PC_SPEC_VECTOR_${sessionData[0].vector.replace(
                              messageRegexPattern,
                              '_'
                            )}`
                          )
                        )
                      : '--'
                  }
                />
                <SpecificationItem
                  label={intl.formatMessage(messages.AMOUNT)}
                  value={
                    sessionData[0].amount
                      ? intl.formatMessage(
                          checkValidKey(
                            messages[
                              `PC_SPEC_AMOUNT_${sessionData[0].amount.replace(
                                messageRegexPattern,
                                '_'
                              )}_UG`
                            ],
                            `PC_SPEC_AMOUNT_${sessionData[0].amount.replace(
                              messageRegexPattern,
                              '_'
                            )}_UG`
                          )
                        )
                      : '--'
                  }
                />
              </>
            ) : state.sessionData[0].physicalMaterial ===
              SpecificationMessages.VIRUS ? (
              <>
                <SpecificationItem
                  label={intl.formatMessage(
                    messages.CONF_SPECIFICATION_PHYSICAL_MATERIAL
                  )}
                  value={intl.formatMessage(
                    messages.PC_SPEC_PHYSICAL_MATERIAL_VIRUS
                  )}
                />
                <SpecificationItem
                  label={intl.formatMessage(
                    messages.CONF_SPECIFICATION_LENTIVIRAL_BACKBONE
                  )}
                  value={
                    sessionData[0].lentiviralBackbone
                      ? intl.formatMessage(
                          checkValidKey(
                            messages[
                              `PC_SPEC_LENTIVIRAL_BACKBONE_${sessionData[0].lentiviralBackbone.replace(
                                messageRegexPattern,
                                '_'
                              )}`
                            ],
                            `PC_SPEC_LENTIVIRAL_BACKBONE_${sessionData[0].lentiviralBackbone.replace(
                              messageRegexPattern,
                              '_'
                            )}`
                          )
                        )
                      : '--'
                  }
                />
                <SpecificationItem
                  label={intl.formatMessage(
                    messages.CONF_VIRAL_TITER_customcrispr_REDESIGNED
                  )}
                  value={
                    sessionData[0].viralTiter
                      ? intl.formatMessage(
                          checkValidKey(
                            messages[
                              `PC_SPEC_VIRAL_TITER_${sessionData[0].viralTiter.replace(
                                messageRegexPattern,
                                '_'
                              )}`
                            ],
                            `PC_SPEC_VIRAL_TITER_${sessionData[0].viralTiter.replace(
                              messageRegexPattern,
                              '_'
                            )}`
                          )
                        )
                      : '--'
                  }
                />
                <SpecificationItem
                  label={intl.formatMessage(
                    messages.CONF_VIRUS_VOLUME_SPECIFICATION_crispgrna
                  )}
                  value={
                    sessionData[0].volume
                      ? intl.formatMessage(
                          checkValidKey(
                            messages[
                              `PC_SPEC_VOLUME_${sessionData[0].volume.replace(
                                messageRegexPattern,
                                '_'
                              )}_ml`
                            ],
                            `PC_SPEC_VOLUME_${sessionData[0].volume.replace(
                              messageRegexPattern,
                              '_'
                            )}_ml`
                          )
                        )
                      : '--'
                  }
                />

                <SpecificationItem
                  label={intl.formatMessage(
                    messages.CONF_VIRUS_ALIQUOTING_SPECIFICATION_crispgrna
                  )}
                  value={
                    sessionData[0].aliquoting
                      ? state.sessionData[0].aliquoting + ' µl'
                      : '--'
                  }
                />
              </>
            ) : state.sessionData[0].physicalMaterial ===
              SpecificationMessages.SRNA ? (
              <>
                <SpecificationItem
                  label={intl.formatMessage(
                    messages.CONF_SPECIFICATION_PHYSICAL_MATERIAL
                  )}
                  value={intl.formatMessage(
                    messages.PC_SPEC_PHYSICAL_MATERIAL_SRNA
                  )}
                />
                <SpecificationItem
                  label={intl.formatMessage(messages.CONF_PURIFICATION)}
                  value={
                    sessionData[0].purification
                      ? intl.formatMessage(
                          checkValidKey(
                            messages[
                              `_PC_SPEC_${sessionData[0].purification.replace(
                                messageRegexPattern,
                                '_'
                              )}`
                            ],
                            `_PC_SPEC_${sessionData[0].purification.replace(
                              messageRegexPattern,
                              '_'
                            )}`
                          )
                        )
                      : '--'
                  }
                />
                <SpecificationItem
                  label={intl.formatMessage(
                    messages.CONF_SPECIFICATION_CRISPR_SPECIES_crisprgrna
                  )}
                  value={
                    sessionData[0].species
                      ? intl.formatMessage(
                          checkValidKey(
                            messages[
                              `PC_SPEC_${sessionData[0].species.replace(
                                messageRegexPattern,
                                '_'
                              )}`
                            ],
                            `PC_SPEC_${sessionData[0].species.replace(
                              messageRegexPattern,
                              '_'
                            )}`
                          )
                        )
                      : '--'
                  }
                />
                <SpecificationItem
                  label={intl.formatMessage(messages.STRUCTURE)}
                  value={
                    sessionData[0].structure
                      ? intl.formatMessage(
                          checkValidKey(
                            messages[
                              `PC_SPEC_${sessionData[0].structure.replace(
                                messageRegexPattern,
                                '_'
                              )}`
                            ],
                            `PC_SPEC_${sessionData[0].structure.replace(
                              messageRegexPattern,
                              '_'
                            )}`
                          )
                        )
                      : '--'
                  }
                />
                <SpecificationItem
                  label={intl.formatMessage(
                    messages.CONF_SCALE_OF_SYNTHESIS_customcrispr
                  )}
                  value={
                    sessionData[0].scale
                      ? intl.formatMessage(
                          checkValidKey(
                            messages[
                              `_PC_SPEC_${sessionData[0].scale.replace(
                                messageRegexPattern,
                                '_'
                              )}_0NM`
                            ],
                            `_PC_SPEC_${sessionData[0].scale.replace(
                              messageRegexPattern,
                              '_'
                            )}_0NM`
                          )
                        )
                      : '--'
                  }
                />
                <SpecificationItem
                  label={intl.formatMessage(
                    messages.CONF_SPECIFICATION_MODIFIED_crisprgrna
                  )}
                  value={
                    sessionData[0].modification
                      ? intl.formatMessage(
                          checkValidKey(
                            messages[
                              `_PC_SPEC_CUSTOM_CRISPR_${sessionData[0].modification.replace(
                                messageRegexPattern,
                                '_'
                              )}`
                            ],
                            `_PC_SPEC_CUSTOM_CRISPR_${sessionData[0].modification.replace(
                              messageRegexPattern,
                              '_'
                            )}`
                          )
                        )
                      : '--'
                  }
                />
                <SpecificationItem
                  label={intl.formatMessage(
                    messages.CONF_SPECIFICATION_FORMAT_crisprgrna
                  )}
                  value={intl.formatMessage(messages._PC_SPEC_DRY)}
                />
              </>
            ) : state.sessionData[0].physicalMaterial ===
              SpecificationMessages.VITRO ? (
              <SpecificationItem
                label={intl.formatMessage(
                  messages.CONF_SPECIFICATION_PHYSICAL_MATERIAL
                )}
                value={intl.formatMessage(
                  messages.PC_SPEC_PHYSICAL_MATERIAL_VITRO
                )}
              />
            ) : null}
          </Grid>
          <Grid item sm={5}>
            <Grid item sm={12} className={classes.subTotal}>
              <FormattedMessage id="CART_SUBTOTAL" defaultMessage="Subtotal" />
              {': '}
              {subTotalPrice}
            </Grid>
            {state.sessionData[0].physicalMaterial ===
              SpecificationMessages.SRNA && (
              <Grid className={classes.shippingInfo}>
                <FormattedMessage {...messages.CONF_DUPLEX_SHIPPING_OPTION} />
                <FormattedMessage {...messages.CONF_AMBIENT_ROOM_TEMP} />
              </Grid>
            )}
            <Grid className={classes.shippingInfo}>
              <FormattedMessage {...messages.CONF_APPLICATIONFREIGHT_TAX} />
            </Grid>
            <Grid className={classes.shippingInfo}>
              <FormattedMessage {...messages.CONF_PROMOTIONAL_CODES} />
            </Grid>
            {state.sessionData[0].physicalMaterial ===
              SpecificationMessages.SRNA &&
              state.sessionData[0].structure ===
                SpecificationMessages.CRRNA && (
                <Grid container className={classes.acknowledgeSpace}>
                  <Grid item sm={1}>
                    <Checkbox
                      color="primary"
                      icon={
                        <div
                          className={clsx(classes.checkbox, {
                            [classes.errorBorder]: agreeTermsError,
                          })}
                        />
                      }
                      checkedIcon={
                        <div className={classes.checkbox}>
                          <CheckMarkIcon fontSize="inherit" color="primary" />
                        </div>
                      }
                      className={classes.checkBoxAcknowledge}
                      checked={acknowledgement}
                      value={acknowledgement}
                      onChange={() => {
                        handleAcknowledgement(!acknowledgement)
                      }}
                    />
                  </Grid>
                  <Grid item sm={11}>
                    *
                    <FormattedMessage
                      {...messages.CONF_CUSTOM_CRISPR_AKNOWLEDGEMENT_TEXT}
                    />
                  </Grid>
                  {agreeTermsError && (
                    <ValidationErrors
                      configuratorsRedesign
                      errors={[
                        intl.formatMessage(
                          messages.CONF_MUST_ACCEPT_ACKNOWLEDGE
                        ),
                      ]}
                    />
                  )}
                </Grid>
              )}
            <Button
              id="customCrispr-addToCart-updateCart-button"
              variant="contained"
              color="secondary"
              size="medium"
              className={classes.addToCart}
              disabled={loading}
              onClick={addToCart}
            >
              {isUpdateCart ? (
                <FormattedMessage {...messages.UPDATE_CART} />
              ) : (
                <FormattedMessage
                  id="ADD_TO_CART"
                  defaultMessage="Add to Cart"
                />
              )}
            </Button>
            <Grid container>
              <Grid item sm={6}>
                <AddToCartItemsMTO
                  dialogOpen={dialogOpen}
                  handleDialogClose={handleDialogClose}
                  price={totalPrice}
                  totalItem={totalItem}
                  urlProduct={sequencePath}
                  companionProducts={companionProductsData}
                />
              </Grid>
            </Grid>
            <DownloadSummary productDetails={productDetails} />
          </Grid>
        </Grid>
      </div>
      {loading && <CircleLoader />}
    </>
  )
}
export default Specifications
